import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { Environment } from '../types/MainContextType';
import { Amplify, Auth } from 'aws-amplify';
import { getAuthConfigForEnvironment } from '../config/environmentConfig';
import { Hub } from 'aws-amplify';

type EnvironmentContextType = {
  currentEnvironment: Environment;
  setCurrentEnvironment: (env: Environment) => void;
  isChangingEnvironment: boolean;
};

const EnvironmentContext = createContext<EnvironmentContextType | undefined>(undefined);

// Default environment is set to production
const DEFAULT_ENVIRONMENT: Environment = 'production';
const STORAGE_KEY = 'idwal-environment';

export const EnvironmentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Initialize state from localStorage or use default
  const [currentEnvironment, setCurrentEnvironment] = useState<Environment>(() => {
    const storedEnvironment = localStorage.getItem(STORAGE_KEY);
    return (storedEnvironment as Environment) || DEFAULT_ENVIRONMENT;
  });
  const [isChangingEnvironment, setIsChangingEnvironment] = useState(false);

  // Handle environment change
  const handleEnvironmentChange = async (env: Environment) => {
    if (env === currentEnvironment) return;
    
    try {
      setIsChangingEnvironment(true);
      
      // Update localStorage
      localStorage.setItem(STORAGE_KEY, env);
      
      // Get new auth config for the selected environment
      const newAuthConfig = getAuthConfigForEnvironment(env);
      
      // Sign out from current user pool
      await Auth.signOut({ global: true });
      
      // Update Amplify configuration with new user pool
      Amplify.configure(newAuthConfig);
      
      // Update state
      setCurrentEnvironment(env);
      
      // Broadcast an event that environment has changed
      Hub.dispatch('environment', { 
        event: 'changed',
        data: { environment: env }
      });
    } catch (error) {
      console.error('Error changing environment:', error);
    } finally {
      setIsChangingEnvironment(false);
    }
  };

  // Initial configuration
  useEffect(() => {
    const authConfig = getAuthConfigForEnvironment(currentEnvironment);
    Amplify.configure(authConfig);
    
    // Listen for auth events
    const authListener = Hub.listen('auth', ({ payload: { event } }) => {
      if (event === 'signIn') {
        console.log('User signed in');
      } else if (event === 'signOut') {
        console.log('User signed out');
      }
    });
    
    return () => {
      Hub.remove('auth', authListener);
    };
  }, []);

  return (
    <EnvironmentContext.Provider 
      value={{ 
        currentEnvironment, 
        setCurrentEnvironment: handleEnvironmentChange,
        isChangingEnvironment
      }}
    >
      {children}
    </EnvironmentContext.Provider>
  );
};

export const useEnvironment = (): EnvironmentContextType => {
  const context = useContext(EnvironmentContext);
  if (context === undefined) {
    throw new Error('useEnvironment must be used within an EnvironmentProvider');
  }
  return context;
};